import type { MealSuggestionPort } from './contracts';
import { MEAL_TEMPLATES } from '../content/catalog';
import type { MealSuggestion, MealTemplate } from '../domain/meals';
import type { FoodContainer, IsoDate } from '../domain/model';

export const PUBLIC_MEAL_STOCK_OPTIONS = [
  { id: 'vegetables', categoryId: 'vegetables', name: 'Légumes variés' },
  { id: 'prepared', categoryId: 'prepared', name: 'Restes de plat' },
  { id: 'meat', categoryId: 'meat', name: 'Viande' },
  { id: 'fish', categoryId: 'fish', name: 'Poisson' },
  { id: 'fruit', categoryId: 'fruit', name: 'Fruits' },
  { id: 'dairy', categoryId: 'dairy', name: 'Produit laitier' },
  { id: 'sauce', categoryId: 'sauce', name: 'Sauce' },
] as const;

export type PublicMealStockId = (typeof PUBLIC_MEAL_STOCK_OPTIONS)[number]['id'];

export interface PublicMealStockEntry {
  readonly id: PublicMealStockId;
  readonly grams: number;
}

export const PUBLIC_MEAL_DEMO_LIMITS = {
  maxEntries: 7,
  minGrams: 50,
  maxGramsPerEntry: 2_000,
  maxTotalGrams: 8_000,
  maxTemplates: 6,
  maxCostUnits: 42,
  maxRunsPerMinute: 6,
  timeoutMs: 250,
} as const;

export interface PublicMealDemoMetadata {
  readonly source: 'frozen-save-canonical-local';
  readonly engineVersion: string;
  readonly templateCount: number;
  readonly selectedCategoryCount: number;
  readonly externalData: false;
  readonly freshness: 'bundled-with-build';
  readonly degraded: false;
}

export type PublicMealDemoResult =
  | {
      readonly state: 'ready';
      readonly suggestions: MealSuggestion[];
      readonly metadata: PublicMealDemoMetadata;
    }
  | {
      readonly state: 'invalid';
      readonly reason: 'empty' | 'duplicate' | 'quantity' | 'cost_cap';
    }
  | {
      readonly state: 'rate_limited';
      readonly retryAfterSeconds: number;
    }
  | {
      readonly state: 'degraded';
      readonly reason: 'timeout' | 'engine_unavailable';
    };

interface PublicMealDemoTiming {
  readonly epochMs: () => number;
  readonly elapsedMs: () => number;
}

const optionById = new Map(PUBLIC_MEAL_STOCK_OPTIONS.map((option) => [option.id, option]));

const isoDate = (epochMs: number): IsoDate => new Date(epochMs).toISOString().slice(0, 10);

const buildContainer = (
  entry: PublicMealStockEntry,
  index: number,
  calculatedOn: IsoDate,
): FoodContainer => {
  const option = optionById.get(entry.id)!;
  const timestamp = `${calculatedOn}T12:00:00.000Z`;
  return {
    id: `public-meal-${option.id}-${index}`,
    householdId: 'public-meal-demo',
    trackingMode: 'internal-only',
    serialNumber: null,
    numberAssignedAt: null,
    numberReleasedAt: null,
    numberAssignmentKind: null,
    numberPolicyVersion: null,
    rotationAlerts: [],
    name: option.name,
    categoryId: option.categoryId,
    locationId: 'public-meal-temporary-stock',
    initialQuantity: entry.grams,
    remainingQuantity: entry.grams,
    unit: 'g',
    storageStartedOn: calculatedOn,
    manufacturerDateType: 'none',
    manufacturerDate: null,
    qualityHorizonOn: calculatedOn,
    horizonRule: null,
    status: 'present',
    incidentStatus: 'none',
    incidentNote: '',
    auditFlag: 'ok',
    notes: '',
    version: 1,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

export class BoundedPublicMealDemoAdapter {
  readonly #attempts: number[] = [];

  constructor(
    private readonly engine: MealSuggestionPort,
    private readonly templates: readonly MealTemplate[] = MEAL_TEMPLATES,
    private readonly timing: PublicMealDemoTiming = {
      epochMs: () => Date.now(),
      elapsedMs: () => performance.now(),
    },
  ) {}

  async suggest(entries: readonly PublicMealStockEntry[]): Promise<PublicMealDemoResult> {
    const validated = this.#validate(entries);
    if (validated != null) return validated;

    const now = this.timing.epochMs();
    const activeAttempts = this.#attempts.filter((attempt) => now - attempt < 60_000);
    this.#attempts.splice(0, this.#attempts.length, ...activeAttempts);
    if (activeAttempts.length >= PUBLIC_MEAL_DEMO_LIMITS.maxRunsPerMinute) {
      const retryAfterSeconds = Math.max(1, Math.ceil((60_000 - (now - activeAttempts[0]!)) / 1_000));
      return { state: 'rate_limited', retryAfterSeconds };
    }
    this.#attempts.push(now);

    const templates = this.templates.slice(0, PUBLIC_MEAL_DEMO_LIMITS.maxTemplates);
    const calculatedOn = isoDate(now);
    const containers = entries.map((entry, index) => buildContainer(entry, index, calculatedOn));
    const startedAt = this.timing.elapsedMs();
    await Promise.resolve();
    try {
      const suggestions = this.engine.suggest(containers, [...templates], [], calculatedOn);
      if (this.timing.elapsedMs() - startedAt > PUBLIC_MEAL_DEMO_LIMITS.timeoutMs) {
        return { state: 'degraded', reason: 'timeout' };
      }
      return {
        state: 'ready',
        suggestions,
        metadata: {
          source: 'frozen-save-canonical-local',
          engineVersion: [...new Set(templates.map((template) => template.version))].join(', '),
          templateCount: templates.length,
          selectedCategoryCount: entries.length,
          externalData: false,
          freshness: 'bundled-with-build',
          degraded: false,
        },
      };
    } catch {
      return { state: 'degraded', reason: 'engine_unavailable' };
    }
  }

  #validate(entries: readonly PublicMealStockEntry[]): PublicMealDemoResult | null {
    if (entries.length === 0 || entries.length > PUBLIC_MEAL_DEMO_LIMITS.maxEntries) {
      return { state: 'invalid', reason: 'empty' };
    }
    if (new Set(entries.map((entry) => entry.id)).size !== entries.length) {
      return { state: 'invalid', reason: 'duplicate' };
    }
    if (entries.some((entry) =>
      !optionById.has(entry.id) ||
      !Number.isInteger(entry.grams) ||
      entry.grams < PUBLIC_MEAL_DEMO_LIMITS.minGrams ||
      entry.grams > PUBLIC_MEAL_DEMO_LIMITS.maxGramsPerEntry,
    )) {
      return { state: 'invalid', reason: 'quantity' };
    }
    const totalGrams = entries.reduce((total, entry) => total + entry.grams, 0);
    const costUnits = entries.length * Math.min(
      this.templates.length,
      PUBLIC_MEAL_DEMO_LIMITS.maxTemplates,
    );
    if (
      totalGrams > PUBLIC_MEAL_DEMO_LIMITS.maxTotalGrams ||
      costUnits > PUBLIC_MEAL_DEMO_LIMITS.maxCostUnits
    ) {
      return { state: 'invalid', reason: 'cost_cap' };
    }
    return null;
  }
}

export type PublicDemoConsent = 'unknown' | 'denied' | 'granted';
export type PublicDemoRailState =
  | 'mobile_not_requested'
  | 'consent_required'
  | 'provider_unavailable'
  | 'filled'
  | 'no_fill'
  | 'timeout'
  | 'error';

type PublicDemoRailLoader = () => Promise<'filled' | 'no_fill'>;

export class ConsentGatedPublicRailAdapter {
  constructor(
    private readonly loader: PublicDemoRailLoader | null = null,
    private readonly timeoutMs = 800,
  ) {}

  async request(input: {
    readonly desktop: boolean;
    readonly consent: PublicDemoConsent;
  }): Promise<PublicDemoRailState> {
    if (!input.desktop) return 'mobile_not_requested';
    if (input.consent !== 'granted') return 'consent_required';
    if (this.loader == null) return 'provider_unavailable';

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    try {
      const timeout = new Promise<'timeout'>((resolve) => {
        timeoutId = setTimeout(() => resolve('timeout'), this.timeoutMs);
      });
      return await Promise.race([this.loader(), timeout]);
    } catch {
      return 'error';
    } finally {
      if (timeoutId != null) clearTimeout(timeoutId);
    }
  }
}

export type PublicMealDemoMetric = 'outil_démarré' | 'résultat_obtenu' | 'cta_app';

export const emitPublicMealDemoMetric = (event: PublicMealDemoMetric): void => {
  window.dispatchEvent(new CustomEvent('dohm:public-demo-metric', {
    detail: { event, product: 'frozen-save' },
  }));
};
